import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { CREATE_QUESTION_PASSAGE_BANK, GET_LIST_QUESTION_PASSAGE_BANK } from 'queries/questionPassageBank'
import { FIND_ONE_SUBJECT } from 'queries/subject'
import { CreateQuestionPassageBankInput } from '__generatedTypes__/globalTypes'
import {
  createQuestionPassageBank,
  createQuestionPassageBankVariables,
} from '__generatedTypes__/createQuestionPassageBank'
import {
  findAllQuestionPassageBank,
  findAllQuestionPassageBankVariables,
} from '__generatedTypes__/findAllQuestionPassageBank'
import { findOneSubject, findOneSubjectVariables } from '__generatedTypes__/findOneSubject'
import { useHistory } from 'hooks/useHistory'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import { LEVEL_DIFFICULTY, LevelOption } from 'pagesNew/QuestionPassages/Form/consts'
import { Form } from '../Form'

export const Create = () => {
  const { navigate } = useLink()
  const [searchParams] = useSearchParams()
  const subjectId = searchParams.get('subjectId')
  const { loadingHistory, populateHistoryWithUser } = useHistory()
  const [availableLevels, setAvailableLevels] = useState<LevelOption[] | null>(null)
  const [findAllQuestionPassageBanks] = useLazyQuery<findAllQuestionPassageBank, findAllQuestionPassageBankVariables>(
    GET_LIST_QUESTION_PASSAGE_BANK
  )
  const [create] = useMutation<createQuestionPassageBank, createQuestionPassageBankVariables>(
    CREATE_QUESTION_PASSAGE_BANK
  )
  const subjectState = useQuery<findOneSubject, findOneSubjectVariables>(FIND_ONE_SUBJECT, {
    variables: {
      id: String(subjectId),
    },
  })
  const subject = subjectState.data?.findOneSubject

  useEffect(() => {
    if (!subject) {
      return
    }

    ;(async () => {
      const { data } = await findAllQuestionPassageBanks({
        variables: {
          params: {
            where: {
              subject: {
                id: subject.id,
              },
            },
          },
        },
        fetchPolicy: 'network-only',
      })

      const usedLevels = data?.findAllQuestionPassageBankCMS.items.map(({ level }) => level)
      const notUsedLevels = LEVEL_DIFFICULTY.filter((l) => !usedLevels?.includes(l.id))
      setAvailableLevels(notUsedLevels)
    })()
  }, [subject])

  const handleSubmit = useCallback(
    async (questionPassageBank: CreateQuestionPassageBankInput) => {
      try {
        const { data } = await create({
          variables: {
            params: {
              ...questionPassageBank,
              history: populateHistoryWithUser(),
              isLive: false, // isLive false by default and can be changed only on CARS QBs list
            },
          },
        })

        if (data?.createQuestionPassageBank.id) {
          navigate(cmsRoutes.CARS_QUESTION_BANK_LIST, undefined, {
            searchParams: new URLSearchParams({
              subjectId: String(subject?.id),
            }),
          })
          toast.success('CARS Question Bank created successfully')
        } else {
          toast.error('Failed to create CARS Question Bank')
        }
      } catch (error) {
        toast.error('Failed to create CARS Question Bank')
      }
    },
    [populateHistoryWithUser, create, navigate, subject]
  )

  useEffect(() => {
    if (!subjectId || (!subjectState.loading && !subject)) {
      toast.error('Invalid data provided for creating CARS Question Bank, redirecting to CARS Question Bank list')
      navigate(cmsRoutes.CARS_QUESTION_BANK_LIST)
    }
  }, [subjectId, subjectState.loading, subject])

  if (!subjectId) {
    return null
  }

  if (subjectState.loading || !subject || loadingHistory || !availableLevels) {
    return <PageLoader />
  }

  return (
    <Form
      questionPassageBank={null}
      subject={subject}
      level={availableLevels[0]}
      availableLevels={availableLevels}
      onSubmit={handleSubmit}
    />
  )
}
